import React, { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { Modal, Button, ListGroup } from "Components/Core";
import { ButtonIcon, SearchControl } from "Components/Shared";
import { getReviewerCoaches } from "Api/Pages/AssetReviewRequestsApi";
import {
  GetReviewerCoachesResponse,
  ReviewerCoach,
} from "Interfaces/Pages/AssetReviewRequestsInterface";

interface ReassignCoachProps {
  show: boolean;
  onClose: () => void;
  onReassign: (coachId: number) => void;
  masterSportId: number;
  currentCoachId?: number;
}

type CoachData = ReviewerCoach;

const ReassignCoach: React.FC<ReassignCoachProps> = ({
  show,
  onClose,
  onReassign,
  masterSportId,
  currentCoachId,
}) => {
  const [coaches, setCoaches] = useState<CoachData[]>([]);
  const [selectedCoachId, setSelectedCoachId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const pageEnd = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const PER_PAGE = 20;

  const fetchCoaches = useCallback(
    async (search: string = "") => {
      setLoading(true);
      try {
        const response: GetReviewerCoachesResponse = await getReviewerCoaches(
          masterSportId || 0,
          search,
          page,
          PER_PAGE
        );
        const list = (response && response.coaches) || [];
        setCoaches((prev) => (page === 1 ? list : [...prev, ...list]));
        const total = response.total_count || 0;
        const loaded = page * PER_PAGE;
        setHasMore(loaded < total);
      } catch (error) {
        console.error("Error fetching coaches:", error);
        setCoaches([]);
      } finally {
        setLoading(false);
      }
    },
    [masterSportId, page]
  );

  const handleSearch = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setPage(1);
      setHasMore(true);
      setCoaches([]);
      await fetchCoaches(value);
    },
    [fetchCoaches]
  );

  const handleCoachClick = (coach: CoachData) => {
    setSelectedCoachId(coach.id);
  };

  const handleReassign = () => {
    if (selectedCoachId) {
      onReassign(selectedCoachId);
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedCoachId(null);
    onClose();
  };

  useEffect(() => {
    if (show) {
      fetchCoaches();
    }
  }, [show, fetchCoaches]);

  // Infinite scroll
  useEffect(() => {
    if (!show) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loading) {
        setPage((p) => p + 1);
      }
    });
    const current = pageEnd.current;
    if (current) observer.observe(current);
    return () => {
      if (current) observer.unobserve(current);
      observer.disconnect();
    };
  }, [hasMore, loading, show]);

  const renderSimpleList = () => {
    return (
      <ListGroup className="ss-list-group gap-2">
        {coaches &&
          coaches.map((coach: CoachData) => {
            const isCurrentCoach = currentCoachId === coach.id;
            const isSelected = selectedCoachId === coach.id;
            return (
              <ListGroup.Item
                key={coach.id}
                className={`p-3 fs-sm text-capitalize ${
                  isCurrentCoach ? "opacity-50" : ""
                }`}
                active={isSelected}
                onClick={() => !isCurrentCoach && handleCoachClick(coach)}
                style={{ cursor: isCurrentCoach ? "not-allowed" : "pointer" }}
              >
                {coach.name}
                {isCurrentCoach && (
                  <span className="ms-2 badge bg-secondary">Current</span>
                )}
              </ListGroup.Item>
            );
          })}
        <div className="d-flex justify-content-center mt-4 fw-bold text-muted">
          {!coaches?.length && !loading && "No Data Found"}
        </div>
      </ListGroup>
    );
  };

  return (
    <Modal className="ss-modal right_modal" show={show} onHide={handleClose}>
      <Modal.Header className="fw-bold fs-sm border-0">
        Reassign Coach
        <ButtonIcon className="fs-5" onClick={handleClose}>
          <FontAwesomeIcon icon={faTimes} />
        </ButtonIcon>
      </Modal.Header>
      <Modal.Body className="d-flex flex-column scroll-y p-0">
        <div className="d-flex justify-content-between gap-2 p-3">
          <SearchControl
            placeholder="Search coaches..."
            label="Search"
            className="flex-1"
            onChange={handleSearch}
          />
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleReassign}
            disabled={!selectedCoachId}
          >
            Reassign Coach
          </Button>
        </div>
        <div className="d-flex flex-column scroll-y flex-1 px-3 pb-3 gap-2">
          {loading ? (
            <div className="d-flex justify-content-center p-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            renderSimpleList()
          )}
          <div ref={pageEnd}></div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ReassignCoach;
